import { useLayout } from "context/layout/layout";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalService } from "services/portal";
import { formatRut as legacyFormatRut } from "utils";
import logo from "../../../assets/white.png";
import { PasswordField } from "components/PasswordField";
import { PasswordStrengthMeter } from "components/PasswordStrengthMeter";
import { useIsMobile } from "hooks/useIsMobile";
import { isValidEmail, isValidRut } from "utils/validations";

export function PortalRegister() {
  const layout = useLayout();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [form, setForm] = useState({
    id_number: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = ({ target: { value, name } }) => {
    if (name === "id_number") {
      return setForm({ ...form, id_number: legacyFormatRut(value) });
    }
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    if (!form.id_number || !form.email || !form.password || !form.confirmPassword) {
      return layout.errorAlert({ message: "Todos los campos son obligatorios" });
    }

    if (!isValidRut(form.id_number)) {
      return layout.errorAlert({ message: "RUT inválido. Verifica el dígito verificador." });
    }

    if (!isValidEmail(form.email)) {
      return layout.errorAlert({ message: "Email inválido" });
    }

    if (form.password.length < 8) {
      return layout.errorAlert({ message: "La contraseña debe tener al menos 8 caracteres" });
    }

    if (form.password !== form.confirmPassword) {
      return layout.errorAlert({ message: "Las contraseñas no coinciden" });
    }

    setLoading(true);
    try {
      await PortalService.register({
        id_number: form.id_number,
        email: form.email,
        password: form.password,
      });
      layout.successAlert({ message: "Registro exitoso. Revisa tu correo para verificar tu cuenta." });
      setTimeout(() => navigate("/portal/login"), 3000);
    } catch (err) {
      const msg = err?.response?.data?.error || "Error al registrarse";
      layout.errorAlert({ message: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "0.75rem 1rem",
    fontSize: "0.95rem",
    border: "1.5px solid #e2e8f0",
    borderRadius: "0.625rem",
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    background: "#fff",
    color: "#1e293b",
    boxSizing: "border-box",
  };

  const handleFocus = (e) => {
    e.target.style.borderColor = "#0353a4";
    e.target.style.boxShadow = "0 0 0 3px rgba(3,83,164,0.1)";
  };

  const handleBlur = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "100vh", width: "100%", overflow: "auto" }}>
      {/* Left panel */}
      <div
        style={{
          width: isMobile ? "100%" : "50%",
          display: "flex",
          flexDirection: "column",
          justifyContent: isMobile ? "flex-start" : "center",
          padding: isMobile ? "1.5rem 1.25rem" : "0 4rem",
          background: "linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #334155 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-120px",
            right: "-120px",
            width: "400px",
            height: "400px",
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        />

        <div style={{ position: "relative", zIndex: 1, maxWidth: "480px" }}>
          <img
            src={logo}
            alt="Profit"
            style={{ width: isMobile ? "120px" : "160px", marginBottom: isMobile ? "0.75rem" : "2.5rem", opacity: 0.95 }}
          />

          {!isMobile && (
            <>
              <h1
                style={{
                  color: "#fff",
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  lineHeight: 1.2,
                  marginBottom: "1rem",
                }}
              >
                Crea tu cuenta
                <br />
                <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400 }}>
                  en el Portal de Clientes
                </span>
              </h1>

              <p
                style={{
                  color: "rgba(255,255,255,0.55)",
                  fontSize: "0.95rem",
                  lineHeight: 1.7,
                }}
              >
                Necesitas estar registrado como cliente de Profit Accounting.
                Usa tu RUT y el correo asociado a tu cuenta.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right panel - form */}
      <div
        style={{
          width: isMobile ? "100%" : "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f8fafc",
          padding: isMobile ? "2rem 0" : 0,
        }}
      >
        <div style={{ width: "100%", maxWidth: "400px", padding: "0 2rem" }}>
          <div style={{ marginBottom: "2rem" }}>
            <p
              style={{
                fontSize: "0.75rem",
                color: "#94a3b8",
                fontWeight: 500,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                marginBottom: "0.5rem",
              }}
            >
              Portal de clientes
            </p>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: "#0f172a",
                marginBottom: "0.25rem",
              }}
            >
              Registro
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#64748b" }}>
              Completa tus datos para crear tu cuenta
            </p>
          </div>

          <form autoComplete="on" onSubmit={(e) => e.preventDefault()}>
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#334155", marginBottom: "0.5rem" }}>
                RUT
              </label>
              <input
                type="text"
                placeholder="Ej: 18.767.225-2"
                value={form.id_number}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                name="id_number"
                maxLength={12}
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#334155", marginBottom: "0.5rem" }}>
                Correo electronico
              </label>
              <input
                type="email"
                placeholder="nombre@empresa.cl"
                value={form.email}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onBlur={handleBlur}
                autoComplete="email"
                name="email"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#334155", marginBottom: "0.5rem" }}>
                Contraseña
              </label>
              <PasswordField
                value={form.password}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                name="password"
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
              />
              <PasswordStrengthMeter password={form.password} />
            </div>

            <div style={{ marginBottom: "1.75rem" }}>
              <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 600, color: "#334155", marginBottom: "0.5rem" }}>
                Confirmar contraseña
              </label>
              <PasswordField
                value={form.confirmPassword}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                name="confirmPassword"
                placeholder="Repite tu contraseña"
                autoComplete="new-password"
              />
            </div>

            <button
              onClick={handleSubmit}
              disabled={loading}
              type="button"
              style={{
                width: "100%",
                padding: "0.8rem",
                fontSize: "0.95rem",
                fontWeight: 600,
                color: "#fff",
                background: loading
                  ? "#64748b"
                  : "linear-gradient(135deg, #0f172a 0%, #334155 100%)",
                border: "none",
                borderRadius: "0.625rem",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                boxShadow: "0 2px 8px rgba(15,23,42,0.25)",
              }}
            >
              {loading ? "Registrando..." : "Crear cuenta"}
            </button>
          </form>

          <p
            style={{
              textAlign: "center",
              fontSize: "0.85rem",
              color: "#64748b",
              marginTop: "1.5rem",
            }}
          >
            Ya tienes cuenta?{" "}
            <Link
              to="/portal/login"
              style={{ color: "#0353a4", textDecoration: "none", fontWeight: 600 }}
            >
              Inicia sesion
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
